/**
 * Locale-aware formatting for the UI (English default, Arabic available).
 * Uses the platform Intl API — no dependency. The mockups use:
 *   dates   10-Aug-2025          amounts  EGP 42,350,000
 *   compact EGP 42.35M           percent  +12%
 */
import { Decimal, type DecimalInput } from '../money/decimal.ts';

export type Locale = 'en' | 'ar';

export const LOCALE_TAGS: Record<Locale, string> = { en: 'en-EG', ar: 'ar-EG' };

export function direction(locale: Locale): 'ltr' | 'rtl' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 10-Aug-2025 (both locales use Latin digits for dates on documents). */
export function formatDate(value: Date | string | null | undefined, locale: Locale = 'en'): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  if (locale === 'ar') {
    return new Intl.DateTimeFormat('ar-EG-u-nu-latn', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
  }
  return `${String(d.getDate()).padStart(2, '0')}-${MONTHS_EN[d.getMonth()]}-${d.getFullYear()}`;
}

export function formatDateTime(value: Date | string | null | undefined, locale: Locale = 'en'): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  const time = new Intl.DateTimeFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', { hour: '2-digit', minute: '2-digit' }).format(d);
  return `${formatDate(d, locale)} ${time}`;
}

export interface MoneyFormatOptions {
  currency?: string;
  decimals?: number;
  /** show "EGP 1,250.00" (default) or just "1,250.00" */
  showCurrency?: boolean;
  locale?: Locale;
}

export function formatMoney(value: DecimalInput | null | undefined, opts: MoneyFormatOptions = {}): string {
  if (value === null || value === undefined || value === '') return '—';
  const { currency = 'EGP', decimals = 2, showCurrency = true, locale = 'en' } = opts;
  const d = Decimal.from(value).round(decimals);
  const num = new Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-US', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(d.toNumber());
  return showCurrency ? `${currency} ${num}` : num;
}

/** 42,350,000 → "42.35M"; 6,870,000 → "6.87M"; 950,000 → "950K" */
export function formatCompact(value: DecimalInput | null | undefined, opts: { currency?: string; locale?: Locale } = {}): string {
  if (value === null || value === undefined) return '—';
  const n = Decimal.from(value).toNumber();
  const abs = Math.abs(n);
  let text: string;
  if (abs >= 1_000_000_000) text = `${trimZeros((n / 1_000_000_000).toFixed(2))}B`;
  else if (abs >= 1_000_000) text = `${trimZeros((n / 1_000_000).toFixed(2))}M`;
  else if (abs >= 1_000) text = `${trimZeros((n / 1_000).toFixed(1))}K`;
  else text = trimZeros(n.toFixed(2));
  return opts.currency ? `${opts.currency} ${text}` : text;
}

function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

export function formatQuantity(value: DecimalInput | null | undefined, decimals = 2, locale: Locale = 'en'): string {
  if (value === null || value === undefined || value === '') return '—';
  const d = Decimal.from(value).round(decimals);
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-EG-u-nu-latn' : 'en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: decimals,
  }).format(d.toNumber());
}

/** "+12%" / "-8%" for KPI trend chips. */
export function formatTrend(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return '—';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${Math.round(pct)}%`;
}

export function percentChange(current: DecimalInput, previous: DecimalInput): number | null {
  const c = Decimal.from(current), p = Decimal.from(previous);
  if (p.isZero()) return null;
  return c.sub(p).div(p.abs(), 6).mul(100).toNumber();
}
